import React, { useEffect, useState } from "react";
import { useUrlSearch } from "@/lib/useUrlSearch";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Package, Plus, Edit, Trash2, Loader2, RefreshCw, Search, SlidersHorizontal } from "lucide-react";
import { formatINR } from "@/lib/utils";
import type { ProductRecord, MasterLookupRecord, ProductAttributeDef } from "@/types";
import { ImageUpload } from "@/components/products/ImageUpload";
import { ProductVariantsSection } from "@/components/products/ProductVariantsSection";
import { SpecFieldsDialog } from "@/components/products/SpecFieldsDialog";
import { UnitConverter } from "@/components/products/UnitConverter";

const GST_RATES = [0, 5, 12, 18, 28];

const EMPTY_FORM = {
  name: "",
  code: "",
  category: "",
  description: "",
  unit: "NOS",
  unit_price: "",
  cost_price: "",
  gst_rate: 5,
  hsn_sac_code: "",
  status: true,
  product_type: "",
  attributes: {} as Record<string, string>,
  stock_mode: "MADE_TO_ORDER" as "READY_STOCK" | "MADE_TO_ORDER",
  available_qty: "",
  reorder_level: "",
  moq: "",
  lead_time_days: "",
};

const selectClass =
  "h-10 sm:h-11 w-full rounded-lg border border-border bg-background px-3 sm:px-3.5 text-sm sm:text-[15px] text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring";

export function ProductsPage() {
  const [masterUnits, setMasterUnits] = useState<MasterLookupRecord[]>([]);
  const [masterCategories, setMasterCategories] = useState<MasterLookupRecord[]>([]);
  const [search, setSearch] = useUrlSearch();
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [productTypes, setProductTypes] = useState<MasterLookupRecord[]>([]);
  const [attrDefs, setAttrDefs] = useState<ProductAttributeDef[]>([]);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<"ALL" | "ACTIVE" | "INACTIVE">("ACTIVE");
  const [actionLoading, setActionLoading] = useState(false);

  // null = closed, "new" = add, record = edit
  const [dialogItem, setDialogItem] = useState<ProductRecord | "new" | null>(null);
  const [deleteItem, setDeleteItem] = useState<ProductRecord | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const { success, error } = useToast();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("product.create");
  const canUpdate = hasPermission("product.update");
  const canDelete = hasPermission("product.delete");
  const canViewCost = hasPermission("product.view_cost");

  useEffect(() => {
    Promise.all([
      api.get("/api/MasterLookup/ByCategory/PRODUCT_UNIT"),
      api.get("/api/MasterLookup/ByCategory/PRODUCT_CATEGORY"),
      api.get("/api/MasterLookup/ByCategory/PRODUCT_TYPE"),
      api.get("/api/Product/AttributeDefs"),
    ])
      .then(([unitRes, catRes, typeRes, defRes]) => {
        setMasterUnits(unwrapArray<MasterLookupRecord>(unitRes));
        setMasterCategories(unwrapArray<MasterLookupRecord>(catRes));
        setProductTypes(unwrapArray<MasterLookupRecord>(typeRes));
        setAttrDefs(unwrapArray<ProductAttributeDef>(defRes));
      })
      .catch(() => undefined);
  }, []);

  const list = usePagedList<ProductRecord>({
    deps: [search, categoryFilter, typeFilter, statusFilter],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const qs = new URLSearchParams({ page: String(page), page_size: "30" });
      if (search.trim()) qs.set("search", search.trim());
      if (categoryFilter !== "ALL") qs.set("category", categoryFilter);
      if (typeFilter !== "ALL") qs.set("product_type", typeFilter);
      if (statusFilter !== "ALL") qs.set("status", statusFilter.toLowerCase());
      try {
        return unwrapPage<ProductRecord>(await api.get(`/api/Product/List?${qs}`));
      } catch (err: any) {
        error("Fetch Error", err.message || "Failed to load products");
        return { rows: [], total: 0, page, page_size: 30 };
      }
    },
  });
  const products = list.rows;
  const loading = list.loading;
  const loadData = list.reload;

  const categories = Array.from(
    new Set([
      ...products.map((p) => p.category).filter((c): c is string => !!c),
      ...masterCategories.map((c) => c.name),
    ])
  ).sort();

  // The API already applied search, category and status
  const filtered = products;

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setDialogItem("new");
  };

  const openEdit = (p: ProductRecord) => {
    setForm({
      name: p.name,
      code: p.code,
      category: p.category || "",
      description: p.description || "",
      unit: p.unit || "NOS",
      unit_price: String(Number(p.unit_price)),
      cost_price: p.cost_price !== null && p.cost_price !== undefined ? String(Number(p.cost_price)) : "",
      gst_rate: Number(p.gst_rate),
      hsn_sac_code: p.hsn_sac_code || "",
      status: p.status !== false,
      product_type: p.product_type || "",
      attributes: Object.fromEntries(Object.entries(p.attributes || {}).map(([k, v]) => [k, String(v)])),
      stock_mode: p.stock_mode || "MADE_TO_ORDER",
      available_qty: p.available_qty !== null && p.available_qty !== undefined ? String(Number(p.available_qty)) : "",
      reorder_level: p.reorder_level !== null && p.reorder_level !== undefined ? String(Number(p.reorder_level)) : "",
      moq: p.moq !== null && p.moq !== undefined ? String(Number(p.moq)) : "",
      lead_time_days: p.lead_time_days !== null && p.lead_time_days !== undefined ? String(p.lead_time_days) : "",
    });
    setDialogItem(p);
  };

  const typeName = (code?: string | null) => productTypes.find((t) => t.code === code)?.name || code || "";
  const formDefs = attrDefs.filter((d) => d.product_type === form.product_type);
  const setAttr = (key: string, value: string) => setForm((f) => ({ ...f, attributes: { ...f.attributes, [key]: value } }));
  // Specs belong to a type, so changing the type starts them afresh
  const changeType = (code: string) => setForm((f) => ({ ...f, product_type: code, attributes: {} }));

  /** Reloads the list and the open product after a photo changes */
  const refreshOpenItem = async () => {
    await loadData();
    if (dialogItem && dialogItem !== "new") {
      try {
        const res: any = await api.get(`/api/Product/ById/${dialogItem.id}`);
        setDialogItem(unwrapObject<ProductRecord>(res, dialogItem));
      } catch {
        /* the list is already fresh */
      }
    }
  };

  // Live margin preview in the dialog
  const previewMargin = (() => {
    const price = Number(form.unit_price);
    const cost = Number(form.cost_price);
    if (!form.cost_price || !price) return null;
    return ((price - cost) / price) * 100;
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.code.trim()) {
      error("Validation Error", "Name and code are required");
      return;
    }
    if (form.unit_price === "" || Number(form.unit_price) < 0) {
      error("Validation Error", "Enter a valid selling price");
      return;
    }

    const missing = formDefs.find((d) => d.required && !String(form.attributes[d.attr_key] ?? "").trim());
    if (missing) {
      error("Validation Error", `${missing.label} is required`);
      return;
    }

    const payload: Record<string, any> = {
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      category: form.category.trim() || undefined,
      description: form.description.trim() || undefined,
      unit: form.unit,
      unit_price: Number(form.unit_price),
      gst_rate: Number(form.gst_rate),
      hsn_sac_code: form.hsn_sac_code.trim() || undefined,
      status: form.status,
      product_type: form.product_type,
      attributes: Object.fromEntries(Object.entries(form.attributes).filter(([, v]) => v !== "")),
      stock_mode: form.stock_mode,
      // Once a product is in inventory its quantity comes from there, so the typed one is left alone
      available_qty: dialogItem && dialogItem !== "new" && dialogItem.inventory_tracked ? undefined : form.stock_mode === "READY_STOCK" && form.available_qty !== "" ? Number(form.available_qty) : null,
      reorder_level: form.reorder_level === "" ? null : Number(form.reorder_level),
      moq: form.moq === "" ? null : Number(form.moq),
      lead_time_days: form.lead_time_days === "" ? null : Math.round(Number(form.lead_time_days)),
    };
    // Only users who can see cost send it (null clears it); others leave the stored cost untouched
    if (canViewCost) {
      payload.cost_price = form.cost_price === "" ? null : Number(form.cost_price);
    }

    setActionLoading(true);
    try {
      if (dialogItem === "new") {
        await api.post("/api/Product/Insert", payload);
        success("Product Created", "Product added to the catalog");
      } else if (dialogItem) {
        await api.put(`/api/Product/Update/${dialogItem.id}`, payload);
        success("Product Updated", "Product saved successfully");
      }
      setDialogItem(null);
      setForm(EMPTY_FORM);
      await loadData();
    } catch (err: any) {
      error("Save Failed", err.message || "Failed to save product");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    setActionLoading(true);
    try {
      const res: any = await api.delete(`/api/Product/Delete/${deleteItem.id}`);
      success("Product Removed", res?.Message || "Product deleted successfully");
      setDeleteItem(null);
      await loadData();
    } catch (err: any) {
      error("Delete Failed", err.message || "Failed to delete product");
    } finally {
      setActionLoading(false);
    }
  };

  const columnCount = 6 + (canViewCost ? 2 : 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Products</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Yarn, fabric, garment and other textile products with specifications, shades, price and GST
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
            className="h-9 gap-1.5"
          >
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {canUpdate && (
            <Button variant="outline" size="sm" onClick={() => setFieldsOpen(true)} className="h-9 gap-1.5">
              <SlidersHorizontal className="size-3.5" />
              Specification fields
            </Button>
          )}
          {canCreate && (
            <Button size="sm" onClick={openAdd} className="h-9 gap-1.5 shadow-sm">
              <Plus className="size-4" />
              Add Product
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
          <Input
            placeholder="Search by name, code or HSN..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-9 text-sm"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className={`${selectClass} sm:w-48`}
        >
          <option value="ALL">All Categories</option>
          {masterCategories.map((c) => (
            <option key={c.code} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className={`${selectClass} sm:w-44`}
        >
          <option value="ALL">All Types</option>
          {productTypes.map((t) => (
            <option key={t.code} value={t.code}>
              {t.name}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          className={`${selectClass} sm:w-36`}
        >
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="ALL">All</option>
        </select>
      </div>

      {/* Table */}
      {loading && products.length === 0 ? (
        <TableSkeleton rows={6} columns={columnCount} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Type / Category</TableHead>
                <TableHead className="text-right">Price / Unit</TableHead>
                {canViewCost && <TableHead className="text-right">Cost</TableHead>}
                {canViewCost && <TableHead className="text-right">Margin</TableHead>}
                <TableHead className="text-right">GST</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => {
                const margin = p.margin_pct !== null && p.margin_pct !== undefined ? Number(p.margin_pct) : null;
                return (
                  <TableRow key={p.id}>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <div className="size-9 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0 overflow-hidden">
                          {p.image_file ? (
                            <img src={`/api/Product/Image/${p.id}?v=${encodeURIComponent(p.updated_on || "")}`} alt="" className="size-full object-cover" loading="lazy" />
                          ) : (
                            <Package className="size-4" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-sm text-foreground">{p.name}</div>
                          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <code className="font-mono">{p.code}</code>
                            {p.hsn_sac_code && <span>· HSN/SAC {p.hsn_sac_code}</span>}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {p.product_type && <div className="text-foreground">{typeName(p.product_type)}</div>}
                      <div className="text-xs">
                        {p.category || (p.product_type ? "" : "-")}
                        {p.inventory_tracked ? (
                          <span className={`ml-1.5 ${Number(p.stock_available ?? 0) > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                            · {Number(p.stock_available ?? 0).toLocaleString("en-IN")} {p.unit} available
                          </span>
                        ) : (
                          p.stock_mode === "READY_STOCK" && <span className="ml-1.5 text-emerald-600 dark:text-emerald-400">· Ready stock</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <span className="font-semibold text-sm">{formatINR(p.unit_price)}</span>
                      <span className="text-xs text-muted-foreground"> / {p.unit}</span>
                    </TableCell>
                    {canViewCost && (
                      <TableCell className="text-right text-sm whitespace-nowrap">
                        {p.cost_price !== null && p.cost_price !== undefined ? formatINR(p.cost_price) : "-"}
                      </TableCell>
                    )}
                    {canViewCost && (
                      <TableCell className="text-right">
                        {margin === null ? (
                          <span className="text-sm text-muted-foreground">-</span>
                        ) : (
                          <Badge variant={margin < 0 ? "destructive" : margin < 15 ? "warning" : "success"}>
                            {margin.toFixed(1)}%
                          </Badge>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="text-right text-sm">{Number(p.gst_rate)}%</TableCell>
                    <TableCell>
                      <Badge variant={p.status !== false ? "success" : "secondary"}>
                        {p.status !== false ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right space-x-1 whitespace-nowrap">
                      {canUpdate && (
                        <Button variant="ghost" size="icon-sm" onClick={() => openEdit(p)} title="Edit Product">
                          <Edit className="size-4 text-muted-foreground hover:text-foreground" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => setDeleteItem(p)}
                          title="Delete Product"
                        >
                          <Trash2 className="size-4 text-red-500 hover:text-red-700" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columnCount} className="text-center py-10 text-muted-foreground text-sm">
                    No products found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={loading} />
        </div>
      )}

      {/* Dialog: Add / Edit */}
      <Dialog open={!!dialogItem} onOpenChange={(open) => !open && setDialogItem(null)}>
        <DialogContent className="sm:max-w-2xl md:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold">{dialogItem === "new" ? "Add Product" : "Edit Product"}</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Prices are per unit and exclude GST.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs sm:text-sm">Product Name *</Label>
                <Input
                  required
                  placeholder="Annual Support Plan"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Code / SKU *</Label>
                <Input
                  required
                  placeholder="SUP-ANNUAL"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  className="font-mono uppercase"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Product Type</Label>
                <select value={form.product_type} onChange={(e) => changeType(e.target.value)} className={selectClass}>
                  <option value="">-- Select Type --</option>
                  {productTypes.map((t) => (
                    <option key={t.code} value={t.code}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {formDefs.length > 0 && (
              <div className="rounded-lg border border-border p-3.5 space-y-3">
                <p className="text-xs sm:text-sm font-semibold">{typeName(form.product_type)} specifications</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {formDefs.map((d) => (
                    <div key={d.id} className="space-y-1.5">
                      <Label className="text-xs sm:text-sm">
                        {d.label}
                        {d.unit_label ? ` (${d.unit_label})` : ""}
                        {d.required ? " *" : ""}
                      </Label>
                      {d.input_type === "SELECT" ? (
                        <select value={form.attributes[d.attr_key] ?? ""} onChange={(e) => setAttr(d.attr_key, e.target.value)} className={selectClass}>
                          <option value="">--</option>
                          {d.options.map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <Input
                          type={d.input_type === "NUMBER" ? "number" : "text"}
                          step={d.input_type === "NUMBER" ? "any" : undefined}
                          value={form.attributes[d.attr_key] ?? ""}
                          onChange={(e) => setAttr(d.attr_key, e.target.value)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Category</Label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className={selectClass}
                >
                  <option value="">-- Select Category --</option>
                  {masterCategories.map((c) => (
                    <option key={c.code} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">HSN / SAC Code</Label>
                <Input
                  placeholder="5208"
                  value={form.hsn_sac_code}
                  onChange={(e) => setForm({ ...form, hsn_sac_code: e.target.value })}
                  className="font-mono"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Selling Price (₹) *</Label>
                <Input
                  required
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.unit_price}
                  onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Unit</Label>
                <select
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  className={selectClass}
                  required
                >
                  <option value="">-- Select Unit of Measure --</option>
                  {masterUnits.map((u) => (
                    <option key={u.code} value={u.code}>
                      {u.name} ({u.code})
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">GST Rate</Label>
                <select
                  value={form.gst_rate}
                  onChange={(e) => setForm({ ...form, gst_rate: Number(e.target.value) })}
                  className={selectClass}
                >
                  {GST_RATES.map((r) => (
                    <option key={r} value={r}>
                      {r}%
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Availability</Label>
                <select
                  value={form.stock_mode}
                  onChange={(e) => setForm({ ...form, stock_mode: e.target.value as typeof form.stock_mode })}
                  className={selectClass}
                >
                  <option value="MADE_TO_ORDER">Made to order</option>
                  <option value="READY_STOCK">Ready stock</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">In stock ({form.unit || "unit"})</Label>
                {dialogItem && dialogItem !== "new" && dialogItem.inventory_tracked ? (
                  <div className="h-10 sm:h-11 rounded-lg border border-border bg-muted/40 px-3 flex flex-col justify-center text-xs leading-tight" title="Worked out from Inventory">
                    <span className="font-semibold text-sm">{Number(dialogItem.stock_available ?? 0).toLocaleString("en-IN")} available</span>
                    <span className="text-muted-foreground">{Number(dialogItem.stock_on_hand ?? 0).toLocaleString("en-IN")} on hand{dialogItem.stock_reserved ? `, ${Number(dialogItem.stock_reserved).toLocaleString("en-IN")} held` : ""}</span>
                  </div>
                ) : (
                  <Input
                    type="number"
                    min={0}
                    step="any"
                    disabled={form.stock_mode !== "READY_STOCK"}
                    value={form.available_qty}
                    onChange={(e) => setForm({ ...form, available_qty: e.target.value })}
                  />
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Reorder at ({form.unit || "unit"})</Label>
                <Input type="number" min={0} step="any" placeholder="No alert" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">MOQ ({form.unit || "unit"})</Label>
                <Input type="number" min={0} step="any" value={form.moq} onChange={(e) => setForm({ ...form, moq: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Lead time (days)</Label>
                <Input type="number" min={0} step="1" value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })} />
              </div>
            </div>

            {canViewCost && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 items-end">
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm">Cost Price (₹)</Label>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="Optional"
                    value={form.cost_price}
                    onChange={(e) => setForm({ ...form, cost_price: e.target.value })}
                  />
                </div>
                <div className="h-9 sm:h-10 flex items-center text-xs sm:text-sm text-muted-foreground">
                  {previewMargin === null ? (
                    "Margin is calculated from cost"
                  ) : (
                    <span>
                      Margin:{" "}
                      <strong className={previewMargin < 0 ? "text-destructive" : "text-foreground"}>
                        {previewMargin.toFixed(1)}%
                      </strong>
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm">Description</Label>
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-xs sm:text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            {dialogItem && dialogItem !== "new" ? (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm">Photo</Label>
                  <ImageUpload
                    route="/api/Product/Image"
                    id={dialogItem.id}
                    hasImage={!!dialogItem.image_file}
                    version={dialogItem.updated_on}
                    canEdit={canUpdate}
                    onChanged={refreshOpenItem}
                  />
                </div>
                <ProductVariantsSection productId={dialogItem.id} unit={form.unit} canEdit={canUpdate} />
                <UnitConverter productId={dialogItem.id} defaultUnit={dialogItem.unit} units={masterUnits} />
              </>
            ) : (
              <p className="text-[11px] text-muted-foreground">Save the product first to add its photo and shades.</p>
            )}

            <div className="flex items-center justify-between rounded-lg border border-border px-3.5 py-2.5">
              <div>
                <Label className="text-xs sm:text-sm">Active</Label>
                <p className="text-[11px] text-muted-foreground">
                  Inactive products can't be added to deals
                </p>
              </div>
              <Switch
                checked={form.status}
                onCheckedChange={(checked) => setForm({ ...form, status: checked })}
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogItem(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Save Product
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <SpecFieldsDialog
        open={fieldsOpen}
        productTypes={productTypes}
        onClose={() => setFieldsOpen(false)}
        onChanged={() => api.get("/api/Product/AttributeDefs").then((r) => setAttrDefs(unwrapArray<ProductAttributeDef>(r))).catch(() => undefined)}
      />

      {/* Dialog: Delete */}
      <Dialog open={!!deleteItem} onOpenChange={(open) => !open && setDeleteItem(null)}>
        <DialogContent className="sm:max-w-lg md:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold text-red-600">Delete Product</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Remove <strong className="text-foreground">{deleteItem?.name}</strong>? If it is used on
              any deal it will be deactivated instead, so existing deals are not affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-3">
            <Button type="button" variant="outline" onClick={() => setDeleteItem(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
